<template>
    <div class="card bl-form-page">
        <PageHeader
            :title="pageTitle"
            :subtitle="pageSubtitle"
            @onClickCloseButton="navigateToList"
        />

        <div class="bl-form-body" :class="{ 'is-loading': pageLoading }">
            <FormSection
                title="Master Budget Line Details"
                icon="fa-solid fa-circle-info"
            >
                <div class="row g-1">
                    <div class="col-12 col-sm-6 col-lg-4">
                        <label class="bl-label">Master Budget Line Name <span class="bl-required">*</span></label>
                        <input
                            v-model="formData.name"
                            type="text"
                            class="form-control bl-input"
                            :class="{ 'is-invalid': fieldError('name') }"
                            placeholder="Enter master budget line name"
                        >
                        <span v-if="fieldError('name')" class="invalid-feedback d-block">{{ fieldError('name') }}</span>
                    </div>
                    <div class="col-12 col-sm-6 col-lg-4">
                        <label class="bl-label">Master Budget Line Code</label>
                        <input
                            v-model="formData.code"
                            type="text"
                            class="form-control bl-input"
                            placeholder="Enter master budget line code"
                        >
                    </div>
                    <div class="col-12 col-sm-6 col-lg-4">
                        <label class="bl-label">Create Date <span class="bl-required">*</span></label>
                        <input
                            v-model="formData.date"
                            type="date"
                            class="form-control bl-input"
                            :class="{ 'is-invalid': fieldError('date') }"
                        >
                        <span v-if="fieldError('date')" class="invalid-feedback d-block">{{ fieldError('date') }}</span>
                    </div>
                    <div class="col-12 col-sm-6 col-lg-4">
                        <label class="bl-label">Start Date <span class="bl-required">*</span></label>
                        <input
                            v-model="formData.start_date"
                            type="date"
                            class="form-control bl-input"
                            :class="{ 'is-invalid': fieldError('start_date') }"
                            :max="formData.end_date"
                        >
                        <span v-if="fieldError('start_date')" class="invalid-feedback d-block">{{ fieldError('start_date') }}</span>
                    </div>
                    <div class="col-12 col-sm-6 col-lg-4">
                        <label class="bl-label">End Date <span class="bl-required">*</span></label>
                        <input
                            v-model="formData.end_date"
                            type="date"
                            class="form-control bl-input"
                            :class="{ 'is-invalid': fieldError('end_date') }"
                            :min="formData.start_date"
                        >
                        <span v-if="fieldError('end_date')" class="invalid-feedback d-block">{{ fieldError('end_date') }}</span>
                    </div>
                    <div class="col-12 col-sm-6 col-lg-4">
                        <label class="bl-label">Interval <span class="bl-required">*</span></label>
                        <v-select
                            v-model="formData.interval"
                            placeholder="Select interval"
                            :options="intervals"
                            label="name"
                            :reduce="option => option.id"
                            :class="{ 'is-invalid-select': fieldError('interval') }"
                        />
                        <span v-if="fieldError('interval')" class="invalid-feedback d-block">{{ fieldError('interval') }}</span>
                    </div>
                    <div v-if="budgetLineId" class="col-12 col-sm-6 col-lg-4">
                        <label class="bl-label">Status</label>
                        <v-select
                            v-model="formData.status"
                            placeholder="Select status"
                            :options="statusList"
                            label="name"
                            :reduce="option => option.id"
                        />
                    </div>
                </div>
            </FormSection>

            <FormSection
                title="Master Budget Line Accounts"
                icon="fa-solid fa-sitemap"
            >
                <template #actions>
                    <template v-if="formData.id">
                        <div v-if="! hasTabData" class="bl-template-select">
                            <AsyncSelect
                                :key="currentTabTemplateType"
                                v-model="selectedTemplate"
                                placeholder="Apply a template"
                                :additional-query="{ template_type: currentTabTemplateType }"
                                :api-service="fetchTemplates"
                            />
                        </div>
                        <button
                            v-if="hasTabData && ! isTemplateSaved"
                            type="button"
                            class="btn bl-btn bl-btn-ghost"
                            @click="onClickMakeTemplate"
                        >
                            <i class="fa-regular fa-copy"></i>
                            <span>Make Template</span>
                        </button>
                        <button
                            v-if="hasTabData && isTemplateSaved"
                            type="button"
                            class="btn bl-btn bl-btn-danger"
                            :disabled="removingTemplate"
                            @click="onClickRemoveTemplate"
                        >
                            <i class="fa-regular fa-trash-can"></i>
                            <span>Remove Template</span>
                        </button>
                        <button type="button" class="btn bl-btn bl-btn-primary" @click="onClickNewAccountGroup">
                            <i class="fa-solid fa-plus"></i>
                            <span>Add Group</span>
                        </button>
                    </template>
                </template>

                <template v-if="formData.id">
                    <div class="bl-tabs" role="tablist">
                        <button
                            v-for="tab in tabs"
                            :key="tab.id"
                            type="button"
                            role="tab"
                            class="bl-tab"
                            :class="{ 'is-active': accountType === tab.id }"
                            @click="onClickTab(tab)"
                        >{{ tab.name }}</button>
                    </div>

                    <div class="bl-tree" :class="{ 'is-loading': treeLoading }">
                        <div class="row d-flex flex-wrap justify-content-between align-items-center">
                            <TreeBrowser
                                v-if="hasTreeData"
                                :key="accountType + treeKey"
                                :groupData="projectAccountList.group"
                                :endpointData="projectAccountList.endpoint"
                                :accountType="accountType"
                                :projectId="budgetLineId"
                                @onClickAddGroup="onAddGroup"
                                @onGroupUpdate="onGroupUpdate"
                                @onClickAddAccount="onAddAccount"
                                @onAccountUpdate="onAccountUpdate"
                                @paginationEmit="onRootPageChange"
                            />
                        </div>
                        <EmptyState
                            v-if="! treeLoading && ! hasTreeData"
                            compact
                            icon="fa-solid fa-sitemap"
                            :title="'No ' + accountTypeName + ' accounts yet'"
                            message="Add a group or apply a template to get started."
                        />
                    </div>
                </template>

                <EmptyState
                    v-if="! formData.id"
                    compact
                    icon="fa-solid fa-lock"
                    title="Save the master budget line first"
                    message="Accounts can be added once the master budget line has been saved."
                />
            </FormSection>
        </div>

        <div class="bl-form-footer">
            <button type="button" class="btn bl-btn bl-btn-ghost" :disabled="isSaving" @click="navigateToList">Cancel</button>
            <button type="button" class="btn bl-btn bl-btn-outline" :disabled="isSaving" @click="handleSubmit('new')">
                <span v-if="savingAction === 'new'" class="spinner-border spinner-border-sm"></span>
                <span>Save & New</span>
            </button>
            <button type="button" class="btn bl-btn bl-btn-primary" :disabled="isSaving" @click="handleSubmit('save')">
                <span v-if="savingAction === 'save'" class="spinner-border spinner-border-sm"></span>
                <span>Save</span>
            </button>
        </div>

        <AddTempleModal
            v-if="$store.state.isModalOpenTwo"
            modalOpen="isModalOpenTwo"
            :templateableId="parseInt(formData.id)"
            :templateType="currentTabTemplateType"
            @onSubmit="onTemplateSaved"
        />

        <ProjectAccountTemplateModal
            v-if="showTemplatePreviewModal"
            :template="selectedTemplate"
            :accountType="templatePreviewAccountType"
            :projectId="formData.id"
            @onClose="closeTemplatePreview"
            @onDeleted="onTemplateDeleted"
            @onSaved="onTemplateApplied"
        />

        <AccountGroupModal
            ref="accountGroup"
            :modal-title="accountTypeName"
            :query="getQuery()"
            :project-account-level="accountLevel"
            :types="homeContent.budget_head_type"
            :project-type="homeContent.project_type"
            :project-account-status="homeContent.project_account_status"
            :account-type="accountType"
            :project-id="formData.id"
            :unitList="unitList"
            :accountHeads="accountHeads"
            :fd6AccountHeads="fd6AccountList"
            :programs="programList"
            :showParent="showParent"
            :form="accountGroupForm"
            :accounts="allAccountGroups"
            @onAccountSave="handleAccountSubmit"
            @onCloseGroupModal="resetAccountForm"
        />
    </div>
</template>

<script setup>
import { computed, inject, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import PageHeader from '@/components/atom/PageHeader.vue'
import FormSection from '@/components/atom/FormSection.vue'
import EmptyState from '@/components/atom/EmptyState.vue'
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue'
import TreeBrowser from '@/components/molecule/procurement/project/TreeBrowser'
import AccountGroupModal from '@/components/molecule/procurement/project/AccountGroupModal'
import ProjectAccountTemplateModal from '@/components/molecule/procurement/project/ProjectAccountTemplateModal.vue'
import AddTempleModal from '@/components/molecule/company/hospital/prescription/AddTempleModal.vue'
import handleProjects from '@/services/modules/procurement/project'
import handleDropdowns from '@/services/modules/dropdowns'
import handleTemplate from '@/services/modules/all-modules/template'
import useDate from '@/services/utils/day'
import { getApiErrorMessage } from '@/services/utils/global.js'

const $store = useStore()
const $route = useRoute()
const $router = useRouter()
const showError = inject('showError')
const showSuccess = inject('showSuccess')
const { currentDate } = useDate()
const { fetchDropdownList } = handleDropdowns()
const { fetchTemplates, removeTemplate, checkTemplate } = handleTemplate()
const {
    createNewProject,
    updateProject,
    fetchProjectForm,
    fetchAccountTree,
    fetchProjectGroups,
    fetchProjectAccount,
    createProjectAccountGroup,
    createProjectAccount,
    updateProjectAccountGroup,
    updateProjectAccount,
} = handleProjects()

const getDefaultForm = () => ({
    name: null,
    code: null,
    date: currentDate(),
    start_date: null,
    end_date: null,
    interval: null,
    status: 'active',
    project_teams: [],
})

const getDefaultAccountForm = () => ({
    linked_item_id: '',
    linked_account_id: '',
    name: '',
    code: '',
    description: '',
    exp_account_type: '',
    project_account_id: '',
    project_account_type: '',
    fd6_account_id: '',
    project_type: '',
    status: 'active',
})

const accountGroup = ref(null)
const pageLoading = ref(false)
const savingAction = ref('')
const errors = ref({})
const formData = ref(getDefaultForm())
const homeContent = ref({})
const unitList = ref([])
const accountHeads = ref([])
const fd6AccountList = ref([])
const programList = ref([])
const accountType = ref('account')
const accountTypeName = ref('Account')
const currentTabTemplateType = ref('project_account_account')
const projectAccountList = ref([])
const treeKey = ref(0)
const treeLoading = ref(false)
const accountLevel = ref('group')
const accountGroupForm = ref(getDefaultAccountForm())
const showParent = ref(false)
const allAccountGroups = ref([])
const lastFetchedType = ref(null)
const showTemplatePreviewModal = ref(false)
const selectedTemplate = ref(null)
const templatePreviewAccountType = ref('')
const templateSavedTabs = ref(new Set())
const removingTemplate = ref(false)

const budgetLineId = computed(() => $route.params.budgetLineId || '')

const pageTitle = computed(() => budgetLineId.value ? 'Edit Master Budget Line' : 'Add Master Budget Line')

const pageSubtitle = computed(() => budgetLineId.value ? 'Update the details and accounts of this master budget line' : 'Fill in the details below to create a new master budget line')

const isSaving = computed(() => savingAction.value !== '')

const isTemplateSaved = computed(() => templateSavedTabs.value.has(currentTabTemplateType.value))

const hasTabData = computed(() => {
    const list = projectAccountList.value
    if (! list) return false
    return Array.isArray(list.group) && list.group.length > 0
})

const hasTreeData = computed(() => {
    if (hasTabData.value) return true
    const endpoint = projectAccountList.value.endpoint
    if (! endpoint) return false
    return Array.isArray(endpoint.data) && endpoint.data.length > 0
})

const tabs = computed(() => {
    const types = homeContent.value.budget_head_type
    if (! types) return []
    return Object.keys(types).map(key => ({ id: key, name: types[key] }))
})

const toOptions = (source) => {
    if (! source) return []
    return Object.keys(source).map(id => ({ id, name: source[id] }))
}

const intervals = computed(() => toOptions(homeContent.value.project_intervals))

const statusList = computed(() => {
    const allowedStatuses = ['active', 'inactive', 'completed']
    return toOptions(homeContent.value.status).filter(option => allowedStatuses.includes(option.id))
})

const extractErrors = (err) => {
    if (! err.response) return {}
    if (! err.response.data) return {}
    return err.response.data.errors || {}
}

const fieldError = (key) => {
    if (! errors.value[key]) return ''
    return errors.value[key][0]
}

const requiredFields = [
    { key: 'name', message: 'Master budget line name is required.' },
    { key: 'date', message: 'Create date is required.' },
    { key: 'start_date', message: 'Start date is required.' },
    { key: 'end_date', message: 'End date is required.' },
    { key: 'interval', message: 'Interval is required.' },
]

const isBlank = (value) => String(value || '').trim() === ''

const isEndBeforeStart = () => {
    if (isBlank(formData.value.start_date)) return false
    if (isBlank(formData.value.end_date)) return false
    return formData.value.end_date < formData.value.start_date
}

const validateForm = () => {
    const found = {}
    requiredFields
        .filter(field => isBlank(formData.value[field.key]))
        .forEach(field => {
            found[field.key] = [field.message]
        })
    if (isEndBeforeStart()) found.end_date = ['End date must be on or after the start date.']
    return found
}

const getQuery = () => {
    const query = `?company_id=${$route.params.companyId}&from_project=1&is_company_budget=1`
    if (! accountType.value) return query
    return query + `&account_type=${accountType.value.toLowerCase()}`
}

const listParams = () => {
    const params = Object.assign({}, $route.params)
    delete params.budgetLineId
    return params
}

const navigateToList = () => {
    $router.push({ name: 'budget-line-setup', params: listParams(), query: Object.assign({}, $route.query, { page: 1 }) })
}

const navigateToEdit = async (id) => {
    await $router.push({ name: 'budget-line-setup-edit', params: Object.assign(listParams(), { budgetLineId: id }), query: $route.query })
    await loadBudgetLine()
}

const resetForm = () => {
    formData.value = getDefaultForm()
    errors.value = {}
    projectAccountList.value = []
    allAccountGroups.value = []
    lastFetchedType.value = null
    selectedTemplate.value = null
    templateSavedTabs.value = new Set()
}

const startNewEntry = async () => {
    resetForm()
    if (! budgetLineId.value) return
    await $router.push({ name: 'budget-line-setup-add', params: listParams(), query: $route.query })
}

const submitRequest = () => {
    if (! budgetLineId.value) return createNewProject(getQuery(), formData.value)
    return updateProject(budgetLineId.value, getQuery(), formData.value)
}

const afterSubmit = async (res, action) => {
    if (! res.status) return showError(res.message)
    const isCreate = ! budgetLineId.value
    showSuccess(budgetLineId.value ? 'Master budget line updated successfully' : 'Master budget line created successfully')
    if (isCreate && action === 'save') return navigateToEdit(res.data.id)
    if (action === 'save') return navigateToList()
    await startNewEntry()
}

const handleSubmit = async (action) => {
    if (isSaving.value) return
    errors.value = validateForm()
    if (Object.keys(errors.value).length) return showError('Please fill in all required fields correctly.')
    savingAction.value = action
    try {
        const res = await submitRequest()
        await afterSubmit(res, action)
    } catch (err) {
        errors.value = extractErrors(err)
        showError(getApiErrorMessage(err))
    } finally {
        savingAction.value = ''
    }
}

const getAccountTree = async () => {
    if (! budgetLineId.value) return
    treeLoading.value = true
    try {
        const res = await fetchAccountTree(budgetLineId.value, getQuery())
        if (! res.status) return
        projectAccountList.value = res.data
        treeKey.value++
    } catch (err) {
        showError(getApiErrorMessage(err))
    } finally {
        treeLoading.value = false
    }
}

const onRootPageChange = async (page) => {
    try {
        const res = await fetchAccountTree(budgetLineId.value, getQuery() + `&page=${page}&offset=1`)
        if (! res.status) return
        projectAccountList.value.endpoint = res.data.endpoint
    } catch (err) {
        showError(getApiErrorMessage(err))
    }
}

const syncTemplateStatus = async (tab) => {
    if (! formData.value.id) return
    const templateType = `project_account_${tab}`
    const query = `?company_id=${$route.params.companyId}&template_type=${templateType}`
    const res = await checkTemplate(formData.value.id, query).catch(() => null)
    if (! res) return
    if (! res.status) return
    if (res.data.exists) return templateSavedTabs.value.add(templateType)
    templateSavedTabs.value.delete(templateType)
}

const onClickTab = async (tab) => {
    if (accountType.value === tab.id) return
    accountType.value = tab.id
    accountTypeName.value = tab.name
    currentTabTemplateType.value = `project_account_${tab.id}`
    projectAccountList.value = []
    selectedTemplate.value = null
    getAccountTree()
    await syncTemplateStatus(tab.id)
}

const getAccountGroups = async () => {
    if (lastFetchedType.value === accountType.value && allAccountGroups.value.length > 0) return
    try {
        const res = await fetchProjectGroups(budgetLineId.value, getQuery())
        if (! res.data) return
        allAccountGroups.value = res.data
        lastFetchedType.value = accountType.value
    } catch (err) {
        showError(getApiErrorMessage(err))
    }
}

const toggleAccountModal = () => {
    if (showParent.value) getAccountGroups()
    if (! accountGroup.value) return
    accountGroup.value.toggleModal()
}

const resetAccountForm = () => {
    accountGroupForm.value = getDefaultAccountForm()
    showParent.value = false
    accountLevel.value = 'group'
}

const onClickNewAccountGroup = () => {
    toggleAccountModal()
    accountLevel.value = 'group'
    accountGroupForm.value.exp_account_type = 'expense'
}

const saveGroup = () => {
    if (accountGroupForm.value.id) return updateProjectAccountGroup(accountGroupForm.value.id, getQuery(), accountGroupForm.value)
    return createProjectAccountGroup(getQuery(), accountGroupForm.value)
}

const saveAccount = () => {
    if (accountGroupForm.value.id) return updateProjectAccount(accountGroupForm.value.id, getQuery(), accountGroupForm.value)
    return createProjectAccount(getQuery(), accountGroupForm.value)
}

const saveAccountOrGroup = () => {
    if (accountLevel.value === 'group') return saveGroup()
    return saveAccount()
}

const requiredLinks = {
    account: { key: 'linked_account_id', message: 'Linked Account Head is required.' },
    item: { key: 'linked_item_id', message: 'Linked Product or Service is required.' },
}

const missingLinkMessage = () => {
    if (accountLevel.value !== 'account') return ''
    const rule = requiredLinks[accountType.value]
    if (! rule) return ''
    if (! isBlank(accountGroupForm.value[rule.key])) return ''
    return rule.message
}

const afterAccountSave = async (res) => {
    if (! res.status) return showError(res.message)
    showSuccess(res.message)
    resetAccountForm()
    toggleAccountModal()
    lastFetchedType.value = null
    await getAccountTree()
}

const handleAccountSubmit = async () => {
    const missingLink = missingLinkMessage()
    if (missingLink) return showError(missingLink)
    accountGroupForm.value.project_id = budgetLineId.value
    try {
        const res = await saveAccountOrGroup()
        await afterAccountSave(res)
    } catch (err) {
        showError(getApiErrorMessage(err))
    }
}

const loadAccountForm = async (id) => {
    try {
        const res = await fetchProjectAccount(id, getQuery())
        if (! res.status) return
        accountGroupForm.value = res.data
    } catch (err) {
        showError(getApiErrorMessage(err))
    }
}

const onAddGroup = (node) => {
    accountGroupForm.value.project_account_id = node.id
    accountGroupForm.value.project_account_type = node.project_account_type
    showParent.value = true
    accountLevel.value = 'group'
    toggleAccountModal()
}

const onGroupUpdate = async (node) => {
    await loadAccountForm(node.id)
    showParent.value = String(node.group_level) !== '1'
    accountLevel.value = 'group'
    toggleAccountModal()
}

const onAddAccount = (node) => {
    accountGroupForm.value.project_account_id = node.id
    accountGroupForm.value.project_account_type = node.project_account_type
    showParent.value = true
    accountLevel.value = 'account'
    toggleAccountModal()
}

const onAccountUpdate = async (node) => {
    await loadAccountForm(node.id)
    showParent.value = true
    accountLevel.value = 'account'
    toggleAccountModal()
}

const onClickMakeTemplate = () => {
    $store.state.isModalOpenTwo = true
}

const onTemplateSaved = () => {
    $store.state.isModalOpenTwo = false
    templateSavedTabs.value.add(currentTabTemplateType.value)
}

const closeTemplatePreview = () => {
    showTemplatePreviewModal.value = false
    selectedTemplate.value = null
}

const onTemplateDeleted = () => {
    closeTemplatePreview()
    if (! templatePreviewAccountType.value) return
    templateSavedTabs.value.delete(`project_account_${templatePreviewAccountType.value}`)
}

const onTemplateApplied = async () => {
    closeTemplatePreview()
    lastFetchedType.value = null
    await getAccountTree()
}

const onClickRemoveTemplate = async () => {
    if (! confirm('Are you sure you want to remove this template?')) return
    removingTemplate.value = true
    const query = `?company_id=${$route.params.companyId}&template_type=${currentTabTemplateType.value}`
    const res = await removeTemplate(query, formData.value.id).catch(() => null)
    removingTemplate.value = false
    if (! res) return showError('Could not remove the template. Please try again.')
    if (! res.status) return showError(res.message)
    showSuccess(res.message)
    templateSavedTabs.value.delete(currentTabTemplateType.value)
}

const applyBudgetLine = (data) => {
    formData.value = Object.assign({}, data, { project_teams: data.project_teams || [] })
}

const loadDropdowns = async () => {
    const query = `?company_id=${$route.params.companyId}&is_home=1&is_unit=1&from_project=1&is_all_account_heads=1&is_fd6_accounts=1&is_program=1`
    try {
        const res = await fetchDropdownList(query)
        if (! res.status) return
        if (! res.data) return
        homeContent.value = res.data.home_data || {}
        unitList.value = res.data.unit_list || []
        accountHeads.value = res.data.account_heads || []
        fd6AccountList.value = res.data.fd6_account_list || []
        programList.value = res.data.program_list || []
    } catch (err) {
        showError(getApiErrorMessage(err))
    }
}

const loadBudgetLine = async () => {
    if (! budgetLineId.value) return
    try {
        const res = await fetchProjectForm(budgetLineId.value, getQuery())
        if (! res.status) return showError(res.message)
        applyBudgetLine(res.data)
        await getAccountTree()
        await syncTemplateStatus(accountType.value)
    } catch (err) {
        showError(getApiErrorMessage(err))
    }
}

watch(selectedTemplate, (template) => {
    if (! template) return
    templatePreviewAccountType.value = accountType.value
    showTemplatePreviewModal.value = true
})

onMounted(async () => {
    pageLoading.value = true
    await loadDropdowns()
    await loadBudgetLine()
    pageLoading.value = false
})
</script>

<style scoped>
.bl-form-page {
    min-height: calc(100vh - 158px);
}
.bl-form-body {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 14px 16px;
    transition: opacity .15s ease;
}
.bl-form-body.is-loading {
    opacity: .55;
    pointer-events: none;
}
.bl-label {
    display: block;
    margin-bottom: 4px;
    font-size: 13px;
    font-weight: 600;
    color: #344054;
}
.bl-required {
    color: #d92d20;
}
.bl-input {
    height: 40px;
    border-radius: 8px;
    border-color: #d0d5dd;
    font-size: 14px;
}
.bl-form-body :deep(.vs__dropdown-toggle) {
    min-height: 40px;
    border-radius: 8px;
    border-color: #d0d5dd;
    font-size: 14px;
}
.bl-form-body :deep(.is-invalid-select .vs__dropdown-toggle) {
    border-color: #ea5455;
}
.bl-form-body .invalid-feedback {
    font-size: 13px;
}
.bl-template-select {
    width: 280px;
}
.bl-tabs {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    margin-bottom: 12px;
    padding: 4px;
    border-radius: 10px;
    background: #f2f4f7;
    width: fit-content;
    max-width: 100%;
}
.bl-tab {
    height: 36px;
    padding: 0 16px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    font-size: 14px;
    font-weight: 500;
    color: #475467;
    white-space: nowrap;
    cursor: pointer;
    transition: all .15s ease;
}
.bl-tab:hover {
    color: #101828;
}
.bl-tab.is-active {
    background: #fff;
    color: #7367f0;
    font-weight: 600;
    box-shadow: 0 1px 3px rgba(16, 24, 40, .1);
}
.bl-tree {
    font-size: 14px;
    transition: opacity .15s ease;
}
.bl-tree.is-loading {
    opacity: .5;
}
.bl-form-footer {
    position: sticky;
    bottom: 0;
    z-index: 5;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    flex-wrap: wrap;
    padding: 12px 16px;
    border-top: 1px solid #eaecf0;
    border-radius: 0 0 .428rem .428rem;
    background: #fff;
    box-shadow: 0 -4px 12px rgba(16, 24, 40, .05);
}
.bl-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 40px;
    min-width: 96px;
    padding: 0 18px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
}
.bl-btn:disabled {
    opacity: .6;
    cursor: not-allowed;
}
.bl-btn-primary {
    background: #7367f0;
    border: 1px solid #7367f0;
    color: #fff;
}
.bl-btn-primary:hover:not(:disabled) {
    background: #5e50ee;
    border-color: #5e50ee;
    color: #fff;
}
.bl-btn-outline {
    background: #fff;
    border: 1px solid #7367f0;
    color: #7367f0;
}
.bl-btn-outline:hover:not(:disabled) {
    background: #f4f3ff;
    color: #5e50ee;
}
.bl-btn-ghost {
    background: #fff;
    border: 1px solid #e4e7ec;
    color: #475467;
}
.bl-btn-ghost:hover:not(:disabled) {
    background: #f9fafb;
    color: #101828;
}
.bl-btn-danger {
    background: #fff;
    border: 1px solid #fecdca;
    color: #b42318;
}
.bl-btn-danger:hover:not(:disabled) {
    background: #fef3f2;
    color: #912018;
}
@media (max-width: 576px) {
    .bl-form-body {
        gap: 12px;
        padding: 12px;
    }
    .bl-template-select {
        width: 100%;
    }
    .bl-tabs {
        width: 100%;
        flex-wrap: nowrap;
        overflow-x: auto;
    }
    .bl-form-footer {
        padding: 12px 16px;
    }
    .bl-form-footer .bl-btn {
        flex: 1 1 0;
        min-width: 0;
        padding: 0 10px;
    }
}
</style>
